import React, { memo, useState, useEffect, useCallback, useRef } from 'react'
import styles from './index.module.less'
import SlickGridComponent from '../../index2'
import { useSetState, useDebounceFn } from 'ahooks'
import { Column, GridOption, Editor, OnCellChangeEventArgs, OnKeyDownEventArgs, OnSelectedRowsChangedEventArgs, SlickgridReactInstance, Editors } from 'slickgrid-react'
import CustomTextEditor from '../../CustomTextEditor'
import { getUUid } from '@/utils/tools'
import { Button, message } from 'antd'

const dataSource = Array.from(Array(10).keys()).map(i => {
  return {
    id: i,
    name: `输入中文或者英文`
  }
})
const QuickEditEditors = ['CustomTextEditor', 'number', 'float', 'input', 'FloatEditor']

const Index = (props: any) => {
  // 🏗️ Excel式实时编辑功能实现原理总结：
  // 
  // 📱 单击激活（无光标）：
  // 1. 单击后通过handleSelectedRowsChanged激活单元格
  // 2. bindCellEvents设置contentEditable=true但不添加'editing' class
  // 3. CSS .slick-cell { caret-color: transparent; } 让光标透明
  // 4. 结果：单元格可接收键盘输入，但看不到光标
  //
  // 🖱️ 双击编辑（有光标）：
  // 1. 双击触发onDblClick事件
  // 2. 调用startEditing()添加'editing' class
  // 3. CSS .slick-cell.editing { caret-color: black; } 让光标可见
  // 4. Selection API将光标精确定位到文本末尾
  // 5. 结果：进入编辑状态，光标显示在文本末尾
  //
  // ⌨️ 键盘输入编辑：
  // 1. 激活状态下直接输入字符会触发handleNonEditingKeyDown
  // 2. 自动进入编辑状态并选中所有文本，实现替换输入效果
  //
  // 🎯 关键技术点：
  // - CSS caret-color控制光标可见性
  // - contentEditable + focus确保能接收输入和中文输入法事件
  // - Selection API精确控制光标位置
  // - 事件监听器处理各种输入场景（中英文、导航键等）

  // 中文输入法状态标记
  const isComposing = useRef(false);

  // SlickGrid 实例引用
  const gridRef = useRef<SlickgridReactInstance | null>(null);

  // 当前激活的单元格元素引用
  const activeEditorRef = useRef<HTMLElement | null>(null);

  // DOM 变化监听器引用
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  // 当前激活单元格的位置
  const activeCellPosition = useRef<{ row: number, cell: number } | null>(null);

  // 编辑状态管理：单独保存编辑相关信息，避免引用混乱
  const editingStateRef = useRef<{
    cell: HTMLElement | null;    // 正在编辑的单元格DOM元素
    row: number;                 // 正在编辑的行号
    cellIndex: number;           // 正在编辑的列号
    originalValue: string;       // 原始文本值
    originalInnerHTML: string;   // 原始完整HTML结构
    isEditing: boolean;          // 是否正在编辑状态
  }>({
    cell: null,
    row: -1,
    cellIndex: -1,
    originalValue: '',
    originalInnerHTML: '',
    isEditing: false
  });

  const [state, setState] = useSetState({
    loading: false,
    height: 400,
    dataSource: dataSource,
    modalVisible: false,
    changedCellValue: '',
  })

  // 自定义样式：控制光标显示 - 实现类似Excel的交互效果
  // 原理解析：
  // 1. 默认状态：caret-color: transparent 让光标透明（不可见）
  // 2. 编辑状态：当单元格添加 'editing' class时，caret-color: black 让光标可见
  // 3. 这样实现了：单击激活单元格时看不到光标，输入时才显示光标的效果
  //
  // 🔄 CSS规则优先级：
  // 单击后 → <div class="slick-cell"> → 应用 .slick-cell 规则 → caret-color: transparent
  // 双击后 → <div class="slick-cell editing"> → 应用 .slick-cell.editing 规则 → caret-color: black
  const customStyles = document.createElement('style');
  customStyles.textContent = `
    .slick-cell {
      caret-color: transparent !important;  /* 默认状态：光标透明，看不见 */
    }
    .slick-cell.editing {
      caret-color: black !important;        /* 编辑状态：光标变为黑色可见 */
      outline: none !important;             /* 移除浏览器默认的焦点轮廓 */
    }
  `;
  document.head.appendChild(customStyles);

  const gridOptions: GridOption = {
    gridWidth: '100%',
    autoEdit: false, // 禁用默认的编辑行为
    enableCellNavigation: true,
    enableFiltering: true,
    enableTreeData: true,
    multiColumnSort: false,
    enableExcelCopyBuffer: false,
    enableRowSelection: true,
    autoResize: {
      container: '#indicator_zjzb_table_container',
      rightPadding: 0,
      bottomPadding: 0,
      calculateAvailableSizeBy: 'container'
    },
    enableAutoResize: true,
    treeDataOptions: {
      identifierPropName: 'id',
      columnId: 'name',
      childrenPropName: 'children',
      autoApproveParentItemWhenTreeColumnIsValid: true,
      autoRecalcTotalsOnFilterChange: false,
      excludeChildrenWhenFilteringTree: false,
      initialSort: { columnId: 'sort', direction: 'asc' }
    },
  }

  const columns: Column[] = [
    {
      id: 'id',
      name: '',
      field: 'id',
      minWidth: 60,
      maxWidth: 70,
      cssClass: 'slickgrid_column_number slickgrid-checkbox-center-cls',
      behavior: "select",
      sortable: true,

    },
    {
      id: 'name',
      field: 'name',
      name: '名称1',
      minWidth: 360,
      cssClass: 'slickgrid-edit',
      sortable: true,
      editor: {
        model: CustomTextEditor
      }
    },
    {
      id: 'name2',
      field: 'name2',
      name: '名称2',
      editor: {
        model: CustomTextEditor
      }
    },
    {
      id: 'name3',
      field: 'name3',
      name: '名称3(不可编辑)',
    },
    // 加一列下拉框的列
    {
      id: 'name4',
      field: 'name4',
      name: '下拉列',
      editor: {
        customStructure: {
          label: 'label',
          value: 'value',
        },
        model: Editors.singleSelect,
        collection: [
          { label: '1', value: '1' },
        ]
      }
    }
  ]

  // 保存单元格值到数据源
  // 参数：行号、列号、要保存的值
  const saveCellValueToDataSource = (row: number, cellIndex: number, value: string) => {
    console.log("saveCellValueToDataSource", row, cellIndex, value)
    if (!gridRef.current) return false;
    const grid = gridRef.current.slickGrid;

    const item = grid.getDataItem(row);
    const column = grid.getColumns()[cellIndex];

    if (item && column && item[column.field] !== value) {
      // 更新数据项
      item[column.field] = value;

      // 重要：使用 SlickGrid 的方法来更新数据源，确保内部状态同步
      // 这样排序、过滤等操作不会丢失编辑的数据
      const dataView = gridRef.current.dataView;
      if (dataView && dataView.updateItem) {
        dataView.updateItem(item.id, item);
      } else {
        // 如果没有数据视图，直接使用网格的数据更新方法
        grid.updateRow(row);
      }

      // 标记该行已更改，触发重新渲染
      grid.invalidateRow(row);
      grid.render();

      // 触发 onCellChange 事件通知外部组件
      setTimeout(() => {
        grid.onCellChange.notify({
          row: row,
          cell: cellIndex,
          item,
          column,
          grid
        });
      }, 0);

      return true;
    }
    return false;
  };

  // 完成编辑并保存值（统一的保存入口）
  // 类似Excel的失焦保存机制
  const finishEditingAndSave = () => {
    const editState = editingStateRef.current;
    if (!editState.isEditing || !editState.cell) return;

    const currentValue = editState.cell.textContent || '';
    console.log('📝 完成编辑并保存:', editState.row, editState.cellIndex, currentValue);

    // 保存到数据源（使用具体的行列号，避免引用混乱）
    saveCellValueToDataSource(editState.row, editState.cellIndex, currentValue);

    // 退出编辑状态
    editState.cell.classList.remove('editing');

    // 清除选择，移除光标
    const selection = window.getSelection();
    selection?.removeAllRanges();

    // 重置编辑状态，避免状态残留
    editingStateRef.current = {
      cell: null,
      row: -1,
      cellIndex: -1,
      originalValue: '',
      originalInnerHTML: '',
      isEditing: false
    };
  };

 // 撤销编辑并恢复原始值（ESC键功能）
  // 类似Excel的ESC撤销机制
  const cancelEditingAndRestore = () => {
    const editState = editingStateRef.current;
    if (!editState.isEditing || !editState.cell) return;

    console.log('🚫 撤销编辑，恢复原始值:', editState.row, editState.cellIndex, editState.originalInnerHTML);

    // 🔄 关键操作：恢复单元格完整HTML内容到编辑前的原始值
    // 使用innerHTML而不是textContent，确保自定义单元格结构不丢失
    editState.cell.innerHTML = editState.originalInnerHTML;

    // 退出编辑状态，移除'editing' class让光标变回透明
    editState.cell.classList.remove('editing');

    // 清除选择，移除光标
    const selection = window.getSelection();
    selection?.removeAllRanges();

    // 重置编辑状态，避免状态残留
    editingStateRef.current = {
      cell: null,
      row: -1,
      cellIndex: -1,
      originalValue: '',
      originalInnerHTML: '',
      isEditing: false
    };

    console.log('✅ 编辑已撤销，内容已恢复');
  };

  // 进入编辑模式的通用逻辑：检查单元格是否可编辑并进入编辑状态
  // 抽离公共逻辑，供双击、F2键、字符输入等使用
  const enterEditingMode = (grid: any, row: number, cellIndex: number) => {
    const cell = grid.getCellNode(row, cellIndex) as HTMLElement;
    if (!cell) return false;
    
    const column = grid.getColumns()[cellIndex];
    if (!QuickEditEditors.includes(column?.editor?.model?.name)) {
      return false; // 不是可编辑的自定义文本列
    }

    console.log('📝 进入编辑模式:', row, cellIndex, column.name);

    // 确保单元格已激活
    grid.setActiveCell(row, cellIndex);

    // 绑定编辑事件（如果尚未绑定）
    if (activeEditorRef.current !== cell) {
      bindCellEvents(cell);
      activeEditorRef.current = cell;
      activeCellPosition.current = { row, cell: cellIndex };
    }

    // 进入编辑状态，显示光标并定位到末尾
    startEditing(cell);
    return true;
  };

  // 开始编辑：进入编辑状态并设置光标位置
  const startEditing = (cell: HTMLElement) => {
    console.log('startEditing', cell);
    const grid = gridRef.current?.slickGrid;
    if (!grid) return;

    const activeCell = grid.getActiveCell();
    if (!activeCell) return;

    // 重要：设置编辑状态到editingStateRef，确保状态正确
    editingStateRef.current = {
      cell: cell,
      row: activeCell.row,
      cellIndex: activeCell.cell,
      originalValue: cell.textContent || '',  // 保存原始文本值
      originalInnerHTML: cell.innerHTML || '',  // 保存完整HTML结构
      isEditing: true
    };

    // 🎯 关键操作：添加'editing' class让光标从透明变为可见
    // 之前单击时：.slick-cell { caret-color: transparent; } → 光标透明
    // 现在双击后：.slick-cell.editing { caret-color: black; } → 光标可见
    cell.classList.add('editing');

    // 确保单元格可编辑
    if (cell.contentEditable !== 'true') {
      cell.contentEditable = 'true';
    }

    // 延迟设置光标位置，确保DOM完全更新
    setTimeout(() => {
      // 先聚焦到单元格，激活键盘输入
      cell.focus();

      // 🎯 核心逻辑：将光标移动到文本末尾（类似Excel的双击行为）
      // 使用Selection API来精确控制光标位置
      const range = document.createRange();           // 创建一个Range对象来定义光标位置
      const selection = window.getSelection();        // 获取当前页面的文本选择对象

      try {
        // 分情况处理光标定位：
        if (cell.childNodes.length > 0) {
          // 情况1：单元格有子节点（通常是文本节点）
          const lastNode = cell.childNodes[cell.childNodes.length - 1];  // 获取最后一个子节点
          if (lastNode.nodeType === Node.TEXT_NODE) {
            // 如果最后一个节点是文本节点，将光标设置到文本的末尾
            // setStart(节点, 偏移量) - 偏移量设为文本长度即到末尾
            range.setStart(lastNode, lastNode.textContent?.length || 0);
          } else {
            // 如果最后一个节点不是文本（比如是元素节点），将光标设置到该节点之后
            range.setStartAfter(lastNode);
          }
        } else {
          // 情况2：单元格没有子节点（空单元格），将光标设置到单元格的开始位置
          range.setStart(cell, 0);
        }

        // 关键步骤：
        range.collapse(true);                 // collapse(true) 将范围折叠到起始点，形成光标而不是选择区域
        selection?.removeAllRanges();         // 清除所有现有的选择
        selection?.addRange(range);           // 添加我们设置的新范围，光标就出现在指定位置了

        console.log('✅ 光标已设置到文本末尾');
      } catch (error) {
        console.warn('⚠️ 设置光标位置失败:', error);
        // 备用方案：如果Selection API失败，至少确保单元格获得焦点
        cell.focus();
      }
    }, 0);  // 使用setTimeout(0)确保在DOM更新完成后执行
  };

  // 处理中文输入开始事件
  const handleCompositionStart = (e) => {
    console.log('🎯 handleCompositionStart triggered', {
      target: e.target,
      data: e.data,
      isComposing: isComposing.current,
      activeCell: activeEditorRef.current,
      targetIsActiveCell: e.target === activeEditorRef.current,
      timestamp: Date.now()
    })
    // 标记为中文输入状态，避免中文输入过程中的错误处理
    isComposing.current = true;
  };

  // 处理中文输入结束事件
  const handleCompositionEnd = (e: CompositionEvent) => {
    console.log('🎯 handleCompositionEnd triggered', {
      target: e.target,
      data: e.data,
      targetIsActiveCell: e.target === activeEditorRef.current,
      timestamp: Date.now()
    })
    // 中文输入结束，可以正常处理键盘事件
    isComposing.current = false;
    // 中文输入结束时不立即保存，等待失焦或其他触发事件
  };

  // 处理输入事件：监听用户的实际输入
  // const handleInput = (e: InputEvent) => {
  //   const cell = e.target as HTMLElement;
  //   console.log('🎯 handleInput triggered', {
  //     target: e.target,
  //     data: (e as any).data,
  //     inputType: e.inputType,
  //     isComposing: isComposing.current,
  //     cellText: cell.textContent,
  //     targetIsActiveCell: e.target === activeEditorRef.current,
  //     cellIsEditing: cell.classList.contains('editing'),
  //     editingState: editingStateRef.current,
  //     timestamp: Date.now()
  //   })

  //   // 确保这是当前正在编辑的单元格
  //   if (editingStateRef.current.isEditing && editingStateRef.current.cell === cell) {
  //     console.log('📝 正在编辑中...', cell.textContent);
  //   }
  // };

  // 绑定单元格事件的通用函数
  // 🔍 关键：实现单击激活但不显示光标的核心逻辑
  // 为激活的可编辑单元格绑定输入相关事件
  const bindCellEvents = (cell: HTMLElement) => {
    if (!cell) return;

    console.log("📌 重新绑定事件到单元格", {
      cell,
      cellId: cell.getAttribute('id'),
      cellClass: cell.className,
      contentEditable: cell.contentEditable,
      timestamp: Date.now()
    });

    // 🎯 关键设置：让单元格可编辑但不显示光标
    // contentEditable='true' 让单元格能接收键盘输入和触发输入法事件
    // 但由于CSS中 .slick-cell { caret-color: transparent; }，光标是透明的
    cell.contentEditable = 'true';

    // 移除旧的事件监听器（如果存在），避免重复绑定
    cell.removeEventListener('compositionstart', handleCompositionStart);
    cell.removeEventListener('compositionend', handleCompositionEnd);
    // cell.removeEventListener('input', handleInput);
    cell.removeEventListener('blur', handleCellBlur);

    // 延迟绑定事件，确保 DOM 完全准备好
    setTimeout(() => {
      // 再次确认单元格仍然存在且是激活状态
      if (cell.parentNode && activeEditorRef.current === cell) {
        console.log("📌 延迟绑定事件监听器");

        // 重新确认 contentEditable 状态
        if (cell.contentEditable !== 'true') {
          cell.contentEditable = 'true';
        }

        // 绑定关键事件监听器：
        // - compositionstart/end: 处理中文输入法
        // - input: 监听所有输入变化 
        // - blur: 处理失焦保存
        cell.addEventListener('compositionstart', handleCompositionStart);
        cell.addEventListener('compositionend', handleCompositionEnd);
        // cell.addEventListener('input', handleInput);
        cell.addEventListener('blur', handleCellBlur);

        // 🔑 关键操作：强制聚焦确保输入法能正常工作
        // 聚焦让单元格能接收键盘事件，但因为没有 'editing' class，
        // 所以光标仍然透明（看不见），实现了"激活但无光标"的效果
        if (document.activeElement !== cell) {
          cell.focus();
        }
      }
    }, 0);
  };

  // 处理单元格失焦事件：类似Excel的失焦保存
  const handleCellBlur = (e: FocusEvent) => {
    const cell = e.target as HTMLElement;
    console.log('🔄 单元格失焦', {
      target: e.target,
      relatedTarget: e.relatedTarget,
      targetIsActiveCell: e.target === activeEditorRef.current,
      cellIsEditing: cell.classList.contains('editing'),
      editingState: editingStateRef.current,
      timestamp: Date.now(),
      content: cell.textContent
    });

    // 确保这是当前正在编辑的单元格，避免错误保存
    if (editingStateRef.current.isEditing && editingStateRef.current.cell === cell) {
      console.log('💾 失焦时自动保存编辑内容');
      finishEditingAndSave();
    }
  };

  // 处理 DOM 变化的防抖函数
  // 🚀 性能优化：用防抖避免频繁的 DOM 变化处理
  // 
  // 📊 优化效果对比：
  // 优化前：每次DOM变化都立即处理 → 可能每秒触发几十次
  // 优化后：100ms内的多次变化合并为一次处理 → 每秒最多10次
  // 
  // 🔧 优化原理：
  // 1. 收集阶段：将频繁的DOM变化先存储起来
  // 2. 去重阶段：使用Set和Map避免重复处理相同元素  
  // 3. 批量处理：100ms后统一处理所有变化
  // 4. 减少DOM操作：避免频繁的bindCellEvents调用
  const { run: debouncedHandleMutations } = useDebounceFn(
    (allMutations: MutationRecord[]) => {
      console.log('🔄 处理防抖后的DOM变化，共', allMutations.length, '个变化');

      // 合并处理所有变化，避免重复操作
      const addedCells = new Set<HTMLElement>();
      const removedElements = new Set<HTMLElement>();
      const changedAttributes = new Map<HTMLElement, string[]>();

      // 收集所有变化
      allMutations.forEach((mutation) => {
        // 收集新增的单元格
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const cells = element.classList?.contains('slick-cell')
              ? [element]
              : element.querySelectorAll?.('.slick-cell') || [];

            Array.from(cells).forEach(cell => addedCells.add(cell as HTMLElement));
          }
        });

        // 收集被移除的元素
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            removedElements.add(node as HTMLElement);
          }
        });

        // 收集属性变化
        if (mutation.type === 'attributes') {
          const target = mutation.target as HTMLElement;
          if (target.classList?.contains('slick-cell')) {
            const attrs = changedAttributes.get(target) || [];
            attrs.push(mutation.attributeName || '');
            changedAttributes.set(target, attrs);
          }
        }
      });

      // 批量处理新增的单元格
      addedCells.forEach((cell) => {
        if (activeCellPosition.current && gridRef.current) {
          const { row, cell: cellIndex } = activeCellPosition.current;
          const activeCell = gridRef.current.slickGrid.getCellNode(row, cellIndex);

          if (activeCell === cell) {
            console.log("🔄 检测到激活单元格重新渲染，重新绑定事件");
            bindCellEvents(cell);
            activeEditorRef.current = cell;
          }
        }
      });

      // 批量处理被移除的元素
      removedElements.forEach((element) => {
        if (element === activeEditorRef.current) {
          console.log("🗑️ 激活单元格被移除");
          activeEditorRef.current = null;
        }
      });

      // 批量处理属性变化
      changedAttributes.forEach((attributes, target) => {
        if (target === activeEditorRef.current && attributes.includes('contenteditable')) {
          console.log("🔧 激活单元格 contentEditable 属性变化");
          if (target.contentEditable !== 'true') {
            console.log("🔄 重新设置 contentEditable");
            bindCellEvents(target);
          }
        }
      });
    },
    {
      wait: 100, // 100ms 防抖延迟，平衡性能和响应性
      leading: false, // 不立即执行
      trailing: true, // 延迟后执行
    }
  );

  // 创建 MutationObserver 来监听 DOM 变化
  // 🚀 性能优化版本：使用防抖处理频繁的DOM变化
  const createMutationObserver = () => {
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
    }

    // 临时存储 mutations，用于批量处理
    let pendingMutations: MutationRecord[] = [];

    mutationObserverRef.current = new MutationObserver((mutations) => {
      // 累积 mutations，而不是立即处理
      pendingMutations.push(...mutations);

      // 使用防抖函数处理，避免频繁执行
      debouncedHandleMutations(pendingMutations);

      // 重置待处理的 mutations（注意：防抖函数会复制数组，所以这里可以清空）
      pendingMutations = [];
    });

    // 开始观察，包括属性变化
    const gridContainer = document.getElementById('indicator_zjzb_table_container');
    if (gridContainer) {
      mutationObserverRef.current.observe(gridContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['contenteditable', 'class']
      });
    }
  };

  // 网格初始化完成后的回调：设置各种事件监听
  const gridReady = (reactGrid: SlickgridReactInstance) => {
    gridRef.current = reactGrid;
    const grid = reactGrid.slickGrid;

    // 创建 MutationObserver 来监听DOM变化
    createMutationObserver();

    // 阻止SlickGrid默认编辑器启动，使用我们自定义的编辑逻辑
    grid.onBeforeEditCell.subscribe((e, args) => {
      // 检查当前列是否有 editor 配置
      const column = grid.getColumns()[args.cell];
      console.log('🔒 阻止默认编辑器启动，列:', column.name, '有editor:', !!column.editor);
      console.log("editBefore", QuickEditEditors.includes(column?.editor?.model?.name))
      // 如果是CustomTextEditor，使用我们自己的编辑逻辑
      // 其他类型的编辑器（下拉框、日期选择器等）仍使用原生逻辑
      if (QuickEditEditors.includes(column?.editor?.model?.name)) {
        return false; // 阻止默认编辑器
      }
      return true; // 允许原生编辑器
    });

    // 监听单元格激活变化：主要用于清理之前的编辑状态
    grid.onActiveCellChanged.subscribe((e, args) => {
      console.log('event.onActiveCellChanged')
      if (!args.cell && !args.row) return;

      // 保存当前激活单元格的位置
      activeCellPosition.current = { row: args.row, cell: args.cell };

      const prevEditor = activeEditorRef.current;
      if (prevEditor) {
        // 在切换单元格前，确保之前单元格的数据已保存
        finishEditingAndSave();

        // 清理之前单元格的状态和事件监听器
        prevEditor.contentEditable = 'false';
        prevEditor.removeEventListener('compositionstart', handleCompositionStart);
        prevEditor.removeEventListener('compositionend', handleCompositionEnd);
        // prevEditor.removeEventListener('input', handleInput);
        prevEditor.removeEventListener('blur', handleCellBlur);
        activeEditorRef.current = null;
      }

      // 激活新单元格的逻辑将由 onSelectedRowsChanged 处理
      console.log('📍 单元格激活位置已更新:', args.row, args.cell);
    });

    // 监听键盘事件：处理编辑状态下和非编辑状态下的不同按键行为
    grid.onKeyDown.subscribe((e, args) => {
      const activeCell = grid.getActiveCell();
      if (!activeCell) return;
      const cell = grid.getCellNode(activeCell.row, activeCell.cell) as HTMLElement;
      if (!cell) return;

      // 检查当前列的编辑器类型和编辑状态
      const column = grid.getColumns()[activeCell.cell];
      const isCustomTextEditor = QuickEditEditors.includes(column?.editor?.model?.name)
      const isEditing = isCustomTextEditor && cell.classList.contains('editing');

      // 添加详细的按键调试信息
      console.log('🔧 keydown event', {
        key: e.key,
        keyCode: e.keyCode,
        isComposing: isComposing.current,
        isEditing,
        isCustomTextEditor,
        editorType: column?.editor?.model?.name || 'none',
        timestamp: Date.now()
      });

      let handled = false;

      if (isEditing) {
        // CustomTextEditor 在编辑状态的键盘处理
        handled = handleEditingKeyDown(e, grid, cell, activeCell);
      } else if (isCustomTextEditor) {
        // CustomTextEditor 非编辑状态的键盘处理
        handled = handleNonEditingKeyDown(e, grid, cell, activeCell);
      }
      // 其他编辑器不处理，让它们使用原生逻辑
      if (handled) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    });

    // 🔄 单击 vs 双击 的核心差异对比：
    // 
    // 📱 单击流程：
    // handleSelectedRowsChanged → bindCellEvents → contentEditable=true + focus()
    // ❌ 没有添加 'editing' class
    // 🎨 CSS: .slick-cell { caret-color: transparent; }
    // 🔍 结果：无光标，但能接收输入
    // 
    // 🖱️ 双击流程：  
    // onDblClick → startEditing → cell.classList.add('editing')
    // ✅ 添加了 'editing' class
    // 🎨 CSS: .slick-cell.editing { caret-color: black; }
    // 🔍 结果：有光标，且定位到文本末尾
    //
    // 💡 核心：'editing' class 的有无决定光标是否可见！

    // 监听双击事件：双击直接进入编辑状态（类似Excel）
    grid.onDblClick.subscribe((e, args) => {
      if (enterEditingMode(grid, args.row, args.cell)) {
        e.preventDefault();
        e.stopPropagation();
        console.log('🖱️ 双击单元格进入编辑模式:', args.row, args.cell);
      }
    })
  };

  // 单元格数据变化回调：更新显示的变化信息
  const handleCellChange = (e: CustomEvent<{ eventData: any; args: OnCellChangeEventArgs; }>) => {
    const { eventData, args } = e.detail;
    const { item, row, cell, column } = args
    console.log("单元格发生变化:", item)

    // 更新显示的改变值（用于UI展示）
    const newValue = item[column.field];
    setState({ changedCellValue: `行${row + 1}, 列${column.name}: ${newValue}` });

    // 模拟保存成功
    setTimeout(() => {
      message.success("保存成功")
    }, 200)
  }

  // 处理行选择变化事件：用于激活单元格后的编辑设置
  // 🖱️ 单击流程解析（为什么单击没有光标）：
  // 1. 用户单击单元格
  // 2. SlickGrid触发handleSelectedRowsChanged事件
  // 3. 调用bindCellEvents()设置contentEditable=true并focus()
  // 4. ⚠️ 关键：这里只是激活，没有调用startEditing()
  // 5. ⚠️ 所以单元格没有'editing' class
  // 6. CSS规则：.slick-cell { caret-color: transparent; } 生效
  // 7. 结果：单元格已聚焦可输入，但光标透明（看不见）
  const handleSelectedRowsChanged = (e: CustomEvent<{ eventData: any; args: OnSelectedRowsChangedEventArgs; }>) => {
    console.log('event.handleSelectedRowsChanged')
    console.log("handleSelectedRowsChanged: ", e)
    const { args: { rows, grid }, } = e.detail
    const activeCell = grid.getActiveCell()
    console.log("activeCell", activeCell)
    if (!activeCell) return;

    // 获取激活的单元格DOM元素
    const cell = grid.getCellNode(activeCell.row, activeCell.cell) as HTMLElement;
    if (!cell) return;

    // 检查当前列的编辑器类型
    const column = grid.getColumns()[activeCell.cell];
    if (QuickEditEditors.includes(column?.editor?.model?.name)) {
      console.log('📌 通过行选择事件激活自定义编辑单元格:', cell, column.name);

      // 🔍 关键：只绑定事件，不进入编辑状态
      // bindCellEvents会设置contentEditable=true和focus()，让单元格能接收输入
      // 但是不会添加'editing' class，所以光标保持透明
      bindCellEvents(cell);
      activeEditorRef.current = cell;
      activeCellPosition.current = { row: activeCell.row, cell: activeCell.cell };
    } else {
      console.log('📝 当前列使用原生编辑器或不可编辑:', column.name);
    }
  }

  // 导航到指定单元格的辅助函数
  // 在导航前会自动保存当前编辑的内容
  const navigateToCell = (grid: any, row: number, cell: number) => {
    // 在导航前，如果当前有正在编辑的单元格，先完成编辑
    const currentCell = activeEditorRef.current;
    if (currentCell?.classList.contains('editing')) {
      finishEditingAndSave();
    }
    // 设置新的激活单元格并滚动到可见区域
    grid.setActiveCell(row, cell);
    grid.scrollRowIntoView(row, false);
  };

  // 获取安全的导航位置：确保不超出表格边界
  const getSafeNavigationPosition = (grid: any, currentRow: number, currentCell: number, direction: 'up' | 'down' | 'left' | 'right') => {
    const maxRow = grid.getDataLength() - 1;
    const maxCell = grid.getColumns().length - 1;

    switch (direction) {
      case 'up':
        return { row: Math.max(0, currentRow - 1), cell: currentCell };
      case 'down':
        return { row: Math.min(maxRow, currentRow + 1), cell: currentCell };
      case 'left':
        return { row: currentRow, cell: Math.max(0, currentCell - 1) };
      case 'right':
        return { row: currentRow, cell: Math.min(maxCell, currentCell + 1) };
      default:
        return { row: currentRow, cell: currentCell };
    }
  };

  // 处理编辑状态下的键盘事件
  // 类似Excel的键盘行为：Enter/Tab/方向键都会保存并导航
  const handleEditingKeyDown = (e: any, grid: any, cell: HTMLElement, activeCell: any) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        // 保存当前编辑内容并导航到下一行
        finishEditingAndSave();
        const downPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, 'down');
        navigateToCell(grid, downPos.row, downPos.cell);
        return true;

      case 'Escape':
        e.preventDefault();
        // 🚫 ESC键撤销编辑：恢复原始值而不是保存当前编辑的内容
        cancelEditingAndRestore();
        return true;

      case 'Tab':
        e.preventDefault();
        // 保存当前编辑内容并导航到右侧单元格
        finishEditingAndSave();
        const rightPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, 'right');
        navigateToCell(grid, rightPos.row, rightPos.cell);
        return true;

      // 方向键在编辑状态下也要保存并导航
      case 'ArrowUp':
        e.preventDefault();
        finishEditingAndSave();
        const upPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, 'up');
        navigateToCell(grid, upPos.row, upPos.cell);
        return true;

      case 'ArrowDown':
        e.preventDefault();
        finishEditingAndSave();
        const downArrowPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, 'down');
        navigateToCell(grid, downArrowPos.row, downArrowPos.cell);
        return true;

      case 'ArrowLeft':
        // 智能左方向键：只有在光标在开头时才导航，否则正常移动光标
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (range.startOffset === 0 && range.collapsed) {
            e.preventDefault();
            finishEditingAndSave();
            const leftPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, 'left');
            navigateToCell(grid, leftPos.row, leftPos.cell);
            return true;
          }
        }
        return false;

      case 'ArrowRight':
        // 智能右方向键：只有在光标在末尾时才导航，否则正常移动光标
        const rightSelection = window.getSelection();
        if (rightSelection && rightSelection.rangeCount > 0) {
          const range = rightSelection.getRangeAt(0);
          const textLength = cell.textContent?.length || 0;
          if (range.startOffset === textLength && range.collapsed) {
            e.preventDefault();
            finishEditingAndSave();
            const rightArrowPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, 'right');
            navigateToCell(grid, rightArrowPos.row, rightArrowPos.cell);
            return true;
          }
        }
        return false;
    }
    return false;
  };

  // 处理非编辑状态下的键盘事件
  // 主要处理导航和编辑模式的触发
  const handleNonEditingKeyDown = (e: any, grid: any, cell: HTMLElement, activeCell: any) => {
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

    // 🚫 处理复制粘贴快捷键：允许正常复制粘贴，但不进入编辑状态
    if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
      console.log('📋 允许 Ctrl+C 复制，但不进入编辑状态');
      return false; // 不阻止默认行为，让浏览器正常处理复制
    }

    if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
      console.log('📋 允许 Ctrl+V 粘贴，但不进入编辑状态');
      return false; // 不阻止默认行为，让浏览器正常处理粘贴
    }

    // 处理F2键：进入编辑模式（类似Excel）
    if (e.key === 'F2') {
      e.preventDefault();
      console.log('⌨️ F2键进入编辑模式');
      if (enterEditingMode(grid, activeCell.row, activeCell.cell)) {
        return true;
      }
      return false;
    }

    // 处理方向键导航
    if (navigationKeys.includes(e.key)) {
      e.preventDefault();
      const direction = e.key.replace('Arrow', '').toLowerCase() as 'up' | 'down' | 'left' | 'right';
      const newPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, direction);
      navigateToCell(grid, newPos.row, newPos.cell);
      return true;
    }

    // 处理Enter键：直接跳转到下一行（不进入编辑状态）
    if (e.key === 'Enter') {
      e.preventDefault();
      const downPos = getSafeNavigationPosition(grid, activeCell.row, activeCell.cell, 'down');
      navigateToCell(grid, downPos.row, downPos.cell);
      return true;
    }

    // 处理普通字符输入：类似Excel，直接替换单元格内容
    if (e.key.length === 1 && !isComposing.current) {
      // 检查当前列的编辑器类型
      const column = grid.getColumns()[activeCell.cell];
      if (QuickEditEditors.includes(column?.editor?.model?.name)) {
        console.log("🚀 字符输入，准备进入编辑模式:", e.key);

        // 设置编辑状态到editingStateRef，确保状态准确
        editingStateRef.current = {
          cell: cell,
          row: activeCell.row,
          cellIndex: activeCell.cell,
          originalValue: cell.textContent || '',  // 保存原始文本值
          originalInnerHTML: cell.innerHTML || '',  // 保存完整HTML结构
          isEditing: true
        };

        // 进入编辑状态并选中所有文本，这样输入会替换全部内容
        cell.classList.add('editing');

        // 选中所有文本，实现类似Excel的替换行为
        const range = document.createRange();
        range.selectNodeContents(cell);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        // 不阻止默认行为，让字符正常输入并替换选中的文本
        return false;
      } else if (column.editor) {
        console.log("📝 字符输入触发原生编辑器:", e.key, column.editor.model?.name || 'unknown');
        // 让原生编辑器处理字符输入
        return false;
      } else {
        console.log("🚫 当前列不可编辑，忽略输入:", e.key);
        return true; // 阻止输入
      }
    }

    return false;
  };

  // 获取表格数据的测试函数
  const handleGetTableData = () => {
    const grid = gridRef.current?.slickGrid;
    if (grid) {
      const data = gridRef.current.dataView.getItems();
      console.log("表格数据:", data);
    }
  }

  // 组件卸载时清理 MutationObserver
  useEffect(() => {
    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
    };
  }, []);

  return (<div>

    {/* <div>
      <Button onClick={handleGetTableData}> 获取表格数据</Button>
    </div> */}
    <div className={styles['tableWrapper']} style={{ height: `${state.height}px`, position: 'relative' }} id='indicator_zjzb_table_container'>
      <SlickGridComponent
        loading={state.loading}
        gridId='indicator_zjzb_grid'
        gridOptions={gridOptions}
        columnDefinitions={columns}
        datasetHierarchical={state.dataSource}
        onCellChange={handleCellChange}
        onSelectedRowsChanged={handleSelectedRowsChanged}
        onReactGridCreated={(e) => gridReady(e.detail)}
      />
      <div style={{ padding: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} className="text-ellipsis">
        改变的单元格的值：{state.changedCellValue}
      </div>
    </div>

  </div>)
}

export default Index

